/* oxlint-disable */
'use client';

import React, { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';

import { useRouter } from 'next/navigation';

import Autoplay from 'embla-carousel-autoplay';

import Fade from 'embla-carousel-fade';
import { isMobile } from 'react-device-detect';

// import urlFor from '@/sanity/urlFor'; // TODO: Uncomment when switching back to CMS

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';

import { SkeletonBanner } from '@/components/ui/SkeletonBanner';

// Brand-appropriate blur placeholder (blue/white theme)
const BLUR_DATA_URL = 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAGAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAz/xAAcEAABBAMBAAAAAAAAAAAAAAABAAIDBAUREiH/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AktdQa0XdrIWMjb9aEV//2Q==';

interface BannerCarouselProps {
  banners: any[] | { banners: any[] } | null | undefined;
}

const BannerCarousel = ({ banners }: BannerCarouselProps) => {
  const [bannersSorted, setBannersSorted] = useState<any[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const router = useRouter();

  const handleBannerClick = useCallback((url: string) => {
    router.push(url);
  }, [router]);

  useEffect(() => {
    try {
      // Handle different data structures gracefully
      let bannersArray: any[] = [];

      if (!banners) {
        setBannersSorted([]);
        return;
      }

      // If banners is an array, use it directly
      if (Array.isArray(banners)) {
        bannersArray = banners;
      } else if (banners && typeof banners === 'object' && Array.isArray(banners.banners)) {
        // If banners has a banners property, use that
        bannersArray = banners.banners;
      } else {
        // If neither, set empty array
        bannersArray = [];
      }

      let bannersFiltered: any[] = [];
      if (isMobile) {
        bannersFiltered = bannersArray.filter((banner: any) => { return banner?.isMobile; });
      } else {
        bannersFiltered = bannersArray.filter((banner: any) => { return !banner?.isMobile; });
      }

      const sortedBanners = bannersFiltered.sort((a: any, b: any) => {
        if (a?.orderRank && b?.orderRank) {
          return a.orderRank.localeCompare(b.orderRank);
        }
        return 0; // No sorting if orderRank is missing
      });

      setBannersSorted(sortedBanners);
      setHasError(false);
    } catch (error) {
      console.warn('Error processing banners:', error);
      setHasError(true);
      setBannersSorted([]);
    }
  }, [banners]);

  // Show skeleton if loading or no banners, or if there's an error
  if (hasError || bannersSorted.length === 0) {
    return <SkeletonBanner />;
  }

  return (
    <div className="relative w-full">
      <Carousel
        className="h-full w-full rounded-lg"
        plugins={[Fade(), Autoplay({ delay: 10000 })]}
        opts={{
          align: 'start',
          containScroll: 'trimSnaps',
        }}
      >
        <CarouselContent className="w-full max-lg:m-0 max-lg:p-0">
          {bannersSorted
            .map((banner, index) => {
              // Safety checks for banner properties
              if (!banner || typeof banner !== 'object') {
                return null;
              }

              // Use static image URLs directly
              // Mobile: 215:103 aspect ratio (768x368), Desktop: 1600x400
              const isMobileBanner = banner.isMobile;
              const { imageUrl } = banner; // Direct path to static image
              const bannerId = banner._id || `banner-${index}`;
              const bannerUrl = banner.urlRedirection || '/';

              // Skip banner if no image URL
              if (!imageUrl) {
                return null;
              }

              return (
                <CarouselItem key={bannerId} className="w-full pl-0">
                  <div className="relative h-auto w-full rounded-lg overflow-hidden">
                    <div className="relative w-full h-full">
                      <Image
                        src={imageUrl}
                        alt="banner image"
                        width={isMobileBanner ? 768 : 1600}
                        height={isMobileBanner ? 368 : 400}
                        sizes="(max-width: 768px) 768px, 1600px"
                        className="h-auto w-full"
                        onClick={() => { return handleBannerClick(bannerUrl); }}
                        priority={index === 0}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : 'low'}
                        placeholder="blur"
                        blurDataURL={BLUR_DATA_URL}
                        style={{
                          objectFit: 'cover',
                          objectPosition: 'center',
                        }}
                      />
                    </div>
                  </div>
                </CarouselItem>
              );
            })
            .filter(Boolean)}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default BannerCarousel;
